import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InputField, SecondaryButton } from "@financial-edge/shared";
import api from "../api/api";
import AuthAlert from "../components/auth/AuthAlert";
import AuthContextPanel from "../components/auth/AuthContextPanel";
import AuthShell from "../components/auth/AuthShell";
import PrimaryButton from "../components/layout/PrimaryButton";
import { getAuthErrorCode, getAuthRequestId } from "../utils/auth/authDiagnostics";
import { buildLaunchPath, buildLaunchSearch, getEffectiveLaunchContext } from "../utils/auth/intent";
import { formatDisplayNumber, isValidAuMobile, isValidEmail, normalizePhone, parsePhoneInput } from "../utils/auth/authUtils";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

export default function InvestorAccessRequest() {
    const location = useLocation();
    const navigate = useNavigate();
    const launchContext = getEffectiveLaunchContext(location.search);
    const initialEmail = String(location.state?.email || location.state?.accessDetails?.email || "").trim().toLowerCase();
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        mobile: "",
        email: initialEmail,
        company: "",
    });
    const [loading, setLoading] = useState(false);
    const [submittedRequest, setSubmittedRequest] = useState(null);
    const [error, setError] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [errorRequestId, setErrorRequestId] = useState("");
    const inputClassName =
        "border-lightBorder bg-white shadow-sm transition focus:border-primary focus:ring-2 focus:ring-primary/30";

    useAuthPageMeta("Request iPortal Access");

    const canSubmit = useMemo(() => {
        return Boolean(
            !loading &&
            form.firstName.trim() &&
            form.lastName.trim() &&
            isValidAuMobile(form.mobile) &&
            isValidEmail(form.email) &&
            form.company.trim()
        );
    }, [form, loading]);

    const setField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setError("");
        setErrorCode("");
        setErrorRequestId("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setErrorCode("");
        setErrorRequestId("");

        if (!canSubmit) {
            setError("Complete all details before requesting iPortal access.");
            return;
        }

        setLoading(true);

        try {
            const result = await api.submitInvestorAccessRequest({
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                mobile: normalizePhone(form.mobile),
                email: form.email.trim().toLowerCase(),
                company: form.company.trim(),
                source: launchContext.source || "iportal",
                returnTo: launchContext.returnTo || "/",
            });

            setSubmittedRequest(result.request || { requestId: result.requestId });
        } catch (err) {
            setError(err?.message || "Unable to submit the iPortal access request.");
            setErrorCode(getAuthErrorCode(err) || "");
            setErrorRequestId(getAuthRequestId(err) || "");
        } finally {
            setLoading(false);
        }
    };

    if (submittedRequest) {
        return (
            <AuthShell
                eyebrow="iPortal access"
                title="Request sent for review"
                description="Your details have been sent to the iPortal review team. You will receive an email once access is approved or declined."
                align="center"
                footer={
                    <SecondaryButton
                        onClick={() => navigate(buildLaunchPath("/login", { ...launchContext, intent: "investor" }))}
                        className="text-lg"
                    >
                        Return to sign-in
                    </SecondaryButton>
                }
            >
                <AuthAlert tone="info" title="Review pending">
                    We will email {form.email.trim().toLowerCase()} after the access review is complete.
                </AuthAlert>
                {submittedRequest.requestId ? (
                    <div className="mt-4 inline-flex rounded-full bg-surface px-4 py-2 text-xs font-semibold text-secondary">
                        Request ID: <span className="ml-2 font-mono text-foreground">{submittedRequest.requestId}</span>
                    </div>
                ) : null}
            </AuthShell>
        );
    }

    return (
        <AuthShell
            eyebrow="iPortal access"
            title="Request investor access"
            description="This iPortal sign-in is limited to approved investors. Submit your details and the review team will confirm your access."
            footer={
                <div className="space-y-3 text-sm text-secondary">
                    <button
                        type="button"
                        onClick={() => navigate(`/login${buildLaunchSearch({ ...launchContext, intent: "investor" })}`)}
                        className="font-semibold text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    >
                        Return to iPortal sign-in
                    </button>
                </div>
            }
        >
            <form onSubmit={handleSubmit} noValidate>
                <AuthContextPanel
                    title="Access review"
                    items={[
                        { label: "Workspace", value: "iPortal" },
                        { label: "Review team", value: "Financial Edge AX" },
                    ]}
                    helper="These details are used only to review your iPortal access request."
                />

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <InputField
                        label="First name"
                        placeholder="Philip"
                        value={form.firstName}
                        onChange={(value) => setField("firstName", value)}
                        className="w-full"
                        inputClassName={inputClassName}
                    />
                    <InputField
                        label="Last name"
                        placeholder="Sherman"
                        value={form.lastName}
                        onChange={(value) => setField("lastName", value)}
                        className="w-full"
                        inputClassName={inputClassName}
                    />
                </div>

                <div className="mt-4 space-y-4">
                    <InputField
                        label="Mobile"
                        type="tel"
                        value={form.mobile}
                        parseOnChange={parsePhoneInput}
                        onChange={(value) => setField("mobile", formatDisplayNumber(value))}
                        className="w-full"
                        inputClassName={inputClassName}
                    />
                    <InputField
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={(value) => setField("email", value)}
                        className="w-full"
                        inputClassName={inputClassName}
                    />
                    <InputField
                        label="Company"
                        value={form.company}
                        onChange={(value) => setField("company", value)}
                        className="w-full"
                        inputClassName={inputClassName}
                    />
                </div>

                {error ? (
                    <div className="mt-4 space-y-2">
                        <AuthAlert tone="error" title="Request could not be submitted" live="assertive">
                            {error}
                        </AuthAlert>
                        {errorCode ? (
                            <div className="text-xs text-secondary">
                                Error code: <span className="font-mono text-foreground">{errorCode}</span>
                            </div>
                        ) : null}
                        {errorRequestId ? (
                            <div className="text-xs text-secondary">
                                Request ID: <span className="font-mono text-foreground">{errorRequestId}</span>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div className="mt-8">
                    <PrimaryButton type="submit" disabled={!canSubmit || loading} aria-busy={loading}>
                        {loading ? "Sending request..." : "Request access"}
                    </PrimaryButton>
                </div>
            </form>
        </AuthShell>
    );
}
